import React, { useState } from 'react';
import { Sparkle, CircleNotch, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SEVERITY_STYLES = {
    cosmetic: { bg: '#F4F4F5', fg: '#52525B', border: '#D4D4D8', label: 'Cosmetic' },
    minor:    { bg: '#E8F5FB', fg: '#1787AB', border: '#1FA5CF', label: 'Minor' },
    major:    { bg: '#FEF3C7', fg: '#92400E', border: '#F59E0B', label: 'Major' },
    urgent:   { bg: '#FEE2E2', fg: '#991B1B', border: '#E53935', label: 'URGENT' },
};

const RESPONSIBILITY_LABEL = {
    owner: 'Owner (DIY)',
    builder: 'Builder',
    specialist: 'Specialist trade',
};

/** Inline AI verdict card for a snag. Shows a "What's this?" button when no
 *  analysis exists; shows the verdict card when it does. */
export default function AiAnalysisCard({ snag, onAnalyse, onUpgrade }) {
    const [loading, setLoading] = useState(false);
    const verdict = snag.ai_analysis;

    const run = async () => {
        setLoading(true);
        const tId = toast.loading('Analysing with AI…');
        try {
            const res = await onAnalyse();
            toast.success(
                res?.cached ? 'Loaded cached analysis' : 'AI analysis ready',
                { id: tId },
            );
        } catch (err) {
            if (err?.response?.status === 402) {
                toast.dismiss(tId);
                onUpgrade?.(err.response.data?.detail?.code || 'ai_cap_reached');
            } else {
                toast.error('AI analysis failed. Try again.', { id: tId });
            }
        } finally {
            setLoading(false);
        }
    };

    if (!verdict) {
        return (
            <button
                type="button"
                onClick={run}
                disabled={loading}
                data-testid={`ai-analyse-${snag.id}`}
                className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#1FA5CF] text-[#1787AB] hover:bg-[#E8F5FB] active:scale-95 transition-all duration-150 ${loading ? 'opacity-60 cursor-wait' : ''}`}
            >
                {loading
                    ? <><CircleNotch size={12} weight="bold" className="animate-spin" /> Analysing…</>
                    : <><Sparkle size={12} weight="fill" /> AI: What's this?</>
                }
            </button>
        );
    }

    const sev = SEVERITY_STYLES[verdict.severity] || SEVERITY_STYLES.minor;
    const confPct = Math.round((verdict.confidence ?? 0) * 100);

    return (
        <div
            className="mt-3 border border-[#1FA5CF] bg-gradient-to-br from-white to-[#E8F5FB] p-3"
            data-testid={`ai-verdict-${snag.id}`}
        >
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] text-[#1787AB] font-bold">
                    <Sparkle size={12} weight="fill" /> AI verdict
                </span>
                <span
                    className="px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider border"
                    style={{ background: sev.bg, color: sev.fg, borderColor: sev.border }}
                >
                    {sev.label}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-[#52525B]">
                    {verdict.cost_band || '??'} · {RESPONSIBILITY_LABEL[verdict.responsibility] || verdict.responsibility}
                </span>
                <span className="ml-auto text-[10px] font-mono text-[#52525B]">
                    {confPct}% confident
                </span>
            </div>

            <div className="text-sm text-[#09090B]">
                <div className="font-display font-semibold text-xs uppercase tracking-wider text-[#52525B] mb-1">Likely cause</div>
                <p>{verdict.cause}</p>
            </div>

            <div className="mt-2 text-sm text-[#09090B]">
                <div className="font-display font-semibold text-xs uppercase tracking-wider text-[#52525B] mb-1">Suggested fix</div>
                <p>{verdict.suggested_fix}</p>
            </div>

            {verdict.nhbc_reference && (
                <div className="mt-2 border-t border-[#D4D4D8] pt-2 text-xs text-[#52525B] inline-flex items-start gap-1">
                    <Info size={12} weight="bold" className="mt-0.5 flex-none" />
                    <span><span className="font-mono uppercase tracking-wider">Ref:</span> {verdict.nhbc_reference}</span>
                </div>
            )}

            <div className="mt-2 text-[10px] font-mono text-[#A1A1AA] italic">
                AI estimate — not a structural assessment. Always consult a qualified professional for urgent issues.
            </div>
        </div>
    );
}
